import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowDownRight,
  IconArrowUpRight,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { useUnsavedChanges } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import {
  emptyMovementForm,
  emptyStockItemForm,
  MOVEMENT_KINDS,
  type MovementFormValues,
  movementFormSchema,
  type StockItemFormValues,
  stockItemFormOf,
  stockItemFormSchema,
  toMovementInput,
  toStockItemInput,
} from './forms.js';
import { formatQuantity, formatSignedQuantity, formatUnit, signedQuantity } from './inventory.js';
import { useErrorText } from './MaterialDialogs.js';
import classes from './Materials.module.css';
import type { MaterialsSource, StockEditor } from './source.js';
import type { Material, StockItem, StockMovement } from './types.js';

export interface StockItemDialogProps {
  opened: boolean;
  /** `null`: yeni stok kalemi. */
  item: StockItem | null;
  source: MaterialsSource;
  editor: StockEditor;
  onClose: () => void;
  onSaved: (item: StockItem) => void;
}

/**
 * Gemide stok kalemi (ADR 0026 §1): katalogdan malzeme, yer, en az, en çok ve yeniden sipariş
 * seviyesi. Malzeme sonradan değişmez; hareketler onun defteridir.
 */
export function StockItemDialog({
  opened,
  item,
  source,
  editor,
  onClose,
  onSaved,
}: StockItemDialogProps) {
  const { t } = useTranslation();
  const k = (key: string) => t(`stock.itemForm.${key}`);
  const errorText = useErrorText();
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<Material[]>([]);
  // Seçilen malzemenin birimi: arama değişip seçenekler yenilense de eldeki miktarın yanında kalır
  const [unitOfSelected, setUnitOfSelected] = useState<string | null>(null);
  const form = useForm<StockItemFormValues>({
    resolver: zodResolver(stockItemFormSchema),
    defaultValues: emptyStockItemForm(),
  });
  const { errors, isSubmitting, isDirty } = form.formState;
  // Kaydedilmemiş değişiklikle sayfadan çıkılırken onay istenir (G5-56)
  useUnsavedChanges(opened && isDirty);

  useEffect(() => {
    if (!opened) return;
    setError(null);
    setSearch('');
    setUnitOfSelected(null);
    form.reset(item ? stockItemFormOf(item) : emptyStockItemForm());
  }, [opened, item, form]);

  // Katalog büyük olabilir (5.000 kalem): seçenekler aramayla sunucudan. Kumanya stoğu Provision
  // ekranında lotla açılır; burada yalnız stores & spares (ADR 0122 §4)
  useEffect(() => {
    if (!opened || item) return;
    const timer = setTimeout(() => {
      source
        .materials({
          ...(search.trim() ? { q: search.trim() } : {}),
          materialClass: 'stores',
          pageSize: 50,
        })
        .then((list) => setOptions(list.items))
        .catch(() => setOptions([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [opened, item, search, source]);

  const fieldError = (key: keyof StockItemFormValues) => {
    const message = errors[key]?.message;
    return message ? t(message) : undefined;
  };
  const materialOptions = item
    ? [{ value: item.materialId, label: item.name }]
    : options.map((m) => ({
        value: m.id,
        label: `${m.name}${m.specification ? ` · ${m.specification}` : ''} (${formatUnit(m.unit)})`,
        disabled: m.inStock,
      }));

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const input = toStockItemInput(values);
      const saved = item
        ? await editor.updateItem(item.id, {
            rowVersion: item.rowVersion,
            location: input.location,
            minQty: input.minQty,
            maxQty: input.maxQty,
            reorderLevel: input.reorderLevel,
          })
        : await editor.createItem(input);
      onSaved(saved);
    } catch (failure) {
      setError(errorCode(failure));
    }
  });

  const numberField = (name: 'minQty' | 'maxQty' | 'reorderLevel', required = false) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <NumberInput
          label={k(name)}
          required={required}
          min={0}
          decimalScale={3}
          value={field.value}
          onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
          error={fieldError(name)}
        />
      )}
    />
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={item ? k('editTitle') : k('newTitle')}
      closeButtonProps={{ 'aria-label': k('close') }}
      size="lg"
    >
      <form onSubmit={submit} noValidate>
        <Stack gap="sm">
          {error ? (
            <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
              {errorText(error)}
            </Alert>
          ) : null}
          <Controller
            control={form.control}
            name="materialId"
            render={({ field }) => (
              <Select
                label={k('material')}
                description={item ? k('materialFixed') : k('materialHint')}
                required
                searchable={item === null}
                // Düzenlemede malzeme sabit: salt okunur, gri değil (plan §0.6)
                readOnly={item !== null}
                {...(item ? { rightSection: null } : {})}
                data={materialOptions}
                filter={({ options: list }) => list}
                // İlk odak malzeme aramasında: pencere açılırken odak kapatma düğmesine kaçmaz
                {...(item
                  ? {}
                  : { searchValue: search, onSearchChange: setSearch, 'data-autofocus': true })}
                nothingFoundMessage={k('noMaterial')}
                value={field.value || null}
                onChange={(value) => {
                  setUnitOfSelected(options.find((m) => m.id === value)?.unit ?? null);
                  field.onChange(value ?? '');
                }}
                error={fieldError('materialId')}
              />
            )}
          />
          <TextInput
            label={k('location')}
            {...(item ? { 'data-autofocus': true } : {})}
            {...form.register('location')}
            error={fieldError('location')}
          />
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            {numberField('minQty', true)}
            {numberField('maxQty')}
            {numberField('reorderLevel')}
          </SimpleGrid>
          {item ? null : (
            // Eldeki malzeme tek adımda (ortak 2026-10-29; ADR 0141 §3): kalemle aynı
            // transaction'da "Opening stock" girişi; boşsa yalnız kalem açılır
            <Controller
              control={form.control}
              name="openingQty"
              render={({ field }) => (
                <NumberInput
                  label={k('openingQty')}
                  description={k('openingQtyHint')}
                  min={0}
                  decimalScale={3}
                  value={field.value ?? ''}
                  onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
                  rightSection={
                    <span className={classes.unitSuffix}>{formatUnit(unitOfSelected ?? '')}</span>
                  }
                  rightSectionWidth={48}
                  error={fieldError('openingQty')}
                />
              )}
            />
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {k('cancel')}
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {k('save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export interface MovementDialogProps {
  opened: boolean;
  kind: 'in' | 'out' | 'adjust';
  /** Önceden seçilen kalem. */
  itemId: string | null;
  items: readonly StockItem[];
  /** Gemi yerel günü; gelecek gün girilemez. */
  today: string;
  editor: StockEditor;
  onClose: () => void;
  onSaved: (result: { movement: StockMovement; item: StockItem }) => void;
}

/**
 * Stok girişi ve çıkışı (`gemi-envanter-kumanya.jpeg` "Add Stock In / Out"; ADR 0026 §2): miktar
 * artı girilir, işaret türden gelir; düzeltme işaretli ve gerekçeli. Hareket değiştirilemez;
 * yanlış hareket ters hareketle düzeltilir. Stok sıfırın altına düşemez.
 */
export function MovementDialog(props: MovementDialogProps) {
  const { opened, kind, itemId, items, today, editor, onClose, onSaved } = props;
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`stock.movementForm.${key}`, options);
  const errorText = useErrorText();
  const [error, setError] = useState<string | null>(null);
  const schema = useMemo(() => movementFormSchema(today), [today]);
  const form = useForm<MovementFormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyMovementForm(today, kind, itemId ?? ''),
  });
  const { errors, isSubmitting, isDirty } = form.formState;
  // Kaydedilmemiş değişiklikle sayfadan çıkılırken onay istenir (G5-56)
  useUnsavedChanges(opened && isDirty);

  useEffect(() => {
    if (!opened) return;
    setError(null);
    form.reset(emptyMovementForm(today, kind, itemId ?? ''));
  }, [opened, kind, itemId, today, form]);

  const values = form.watch();
  const selected = items.find((i) => i.id === values.stockItemId) ?? null;
  const after =
    selected && values.quantity !== ''
      ? selected.onHand + signedQuantity(values.kind, values.quantity)
      : null;
  const unitQty = (value: number) =>
    `${formatQuantity(value, locale)} ${formatUnit(selected?.unit ?? '')}`;
  const hint = selected
    ? after !== null
      ? k('onHandAfter', {
          onHand: unitQty(selected.onHand),
          after: unitQty(after),
          location: selected.location ?? '—',
        })
      : k('onHand', { onHand: unitQty(selected.onHand), location: selected.location ?? '—' })
    : undefined;
  const fieldError = (key: keyof MovementFormValues) => {
    const message = errors[key]?.message;
    return message ? t(message) : undefined;
  };

  const submit = form.handleSubmit(async (formValues) => {
    setError(null);
    try {
      onSaved(await editor.move(toMovementInput(formValues, today)));
    } catch (failure) {
      setError(errorCode(failure));
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={k(`title.${values.kind}`)}
      closeButtonProps={{ 'aria-label': k('close') }}
      size="lg"
    >
      <form onSubmit={submit} noValidate>
        <Stack gap="sm">
          {error ? (
            <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
              {error === 'NEGATIVE_STOCK' && selected
                ? k('negative', {
                    onHand: formatQuantity(selected.onHand, locale),
                    unit: formatUnit(selected.unit),
                  })
                : errorText(error)}
            </Alert>
          ) : null}
          <Controller
            control={form.control}
            name="kind"
            render={({ field }) => (
              <SegmentedControl
                aria-label={k('kind')}
                fullWidth
                data={MOVEMENT_KINDS.map((value) => ({ value, label: k(`kinds.${value}`) }))}
                value={field.value}
                onChange={(value) => field.onChange(value)}
              />
            )}
          />
          <Controller
            control={form.control}
            name="stockItemId"
            render={({ field }) => (
              <Select
                label={k('item')}
                required
                searchable
                data={items.map((i) => ({
                  value: i.id,
                  label: `${i.name}${i.specification ? ` · ${i.specification}` : ''}`,
                }))}
                nothingFoundMessage={k('noItem')}
                {...(itemId ? {} : { 'data-autofocus': true })}
                // Eldeki ve hareketten sonraki miktar seçicinin altında (G5-54)
                description={hint}
                inputWrapperOrder={['label', 'input', 'description', 'error']}
                value={field.value || null}
                onChange={(value) => field.onChange(value ?? '')}
                error={fieldError('stockItemId')}
              />
            )}
          />
          {/* Miktar ile tarih aynı satırda, üstten ve alttan hizalı; ipucu girişin altında (G5-52) */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Controller
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <NumberInput
                  label={values.kind === 'adjust' ? k('signedQuantity') : k('quantity')}
                  required
                  decimalScale={3}
                  {...(values.kind === 'adjust' ? {} : { min: 0 })}
                  rightSection={
                    <span className={classes.unitSuffix}>{formatUnit(selected?.unit ?? '')}</span>
                  }
                  rightSectionWidth={48}
                  {...(itemId ? { 'data-autofocus': true } : {})}
                  value={field.value}
                  onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
                  error={fieldError('quantity')}
                />
              )}
            />
            <Controller
              control={form.control}
              name="localDate"
              render={({ field }) => (
                // Gün GG.AA.YYYY, işletim sisteminden bağımsız; gelecek gün yok (G5-53)
                <DayInput
                  label={k('date')}
                  required
                  description={k('dateHint', { today: formatDay(today) })}
                  inputWrapperOrder={['label', 'input', 'description', 'error']}
                  maxDate={today}
                  value={field.value}
                  onChange={(value) => field.onChange(value ?? '')}
                  onBlur={field.onBlur}
                  error={fieldError('localDate')}
                />
              )}
            />
          </SimpleGrid>
          <Textarea
            label={values.kind === 'adjust' ? k('reasonRequired') : k('reason')}
            required={values.kind === 'adjust'}
            autosize
            minRows={2}
            {...form.register('reason')}
            error={fieldError('reason')}
          />
          <Alert color="info" variant="light" role="note" icon={<IconInfoCircle size={18} />}>
            {k('immutable')}
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {k('cancel')}
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {k(`save.${values.kind}`)}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export interface ItemHistoryDialogProps {
  item: StockItem | null;
  source: MaterialsSource;
  onClose: () => void;
  /** Gemide: bu kalem için giriş ya da çıkış penceresini açar (satırda yalnız göz ikonu var). */
  onMovement?: ((kind: 'in' | 'out') => void) | undefined;
}

/**
 * Kalemin hareket geçmişi (yeniden eskiye): tarih, tür, miktar, giren ve gerekçe. Gemide altta
 * bu kaleme giriş ve çıkış düğmeleri.
 */
export function ItemHistoryDialog({ item, source, onClose, onMovement }: ItemHistoryDialogProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`stock.history.${key}`, options);
  const [moves, setMoves] = useState<StockMovement[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!item) return;
    setMoves(null);
    setFailed(false);
    source
      .movements(item.vesselId, { stockItemId: item.id, limit: 100 })
      .then(setMoves)
      .catch(() => setFailed(true));
  }, [item, source]);

  return (
    <Modal
      opened={item !== null}
      onClose={onClose}
      title={item ? k('title', { name: item.name }) : ''}
      closeButtonProps={{ 'aria-label': k('close') }}
      size="xl"
    >
      {failed ? (
        <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
          {k('error')}
        </Alert>
      ) : moves === null ? (
        <Skeleton height={160} />
      ) : moves.length === 0 ? (
        <p className={classes.muted}>{k('empty')}</p>
      ) : (
        <div className={classes.reportTable}>
          <table className={classes.miniTable}>
            <thead>
              <tr>
                <th scope="col">{k('date')}</th>
                <th scope="col">{k('type')}</th>
                <th scope="col" data-numeric>
                  {k('quantity')}
                </th>
                <th scope="col">{k('user')}</th>
                <th scope="col">{k('reason')}</th>
              </tr>
            </thead>
            <tbody>
              {moves.map((move) => (
                <tr key={move.id}>
                  <td className={classes.number}>{formatDay(move.localDate)}</td>
                  <td>{t(`stock.kinds.${move.kind}`, { defaultValue: move.kind })}</td>
                  <td data-numeric>
                    {`${formatSignedQuantity(move.quantity, locale)} ${formatUnit(move.unit)}`}
                  </td>
                  <td>{move.createdByName ?? '—'}</td>
                  <td>{move.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {item && onMovement ? (
        <Group justify="flex-end" mt="md">
          <Button
            variant="light"
            color="green"
            leftSection={<IconArrowDownRight size={16} />}
            aria-label={t('stock.inOut.stockInFor', { name: item.name })}
            onClick={() => onMovement('in')}
          >
            {t('stock.inOut.tabs.in')}
          </Button>
          <Button
            variant="light"
            color="red"
            leftSection={<IconArrowUpRight size={16} />}
            aria-label={t('stock.inOut.stockOutFor', { name: item.name })}
            onClick={() => onMovement('out')}
          >
            {t('stock.inOut.tabs.out')}
          </Button>
        </Group>
      ) : null}
    </Modal>
  );
}
